import type { AxiosError, AxiosRequestConfig } from 'axios';
import { BACKEND_ERROR_CODE } from '@sa/axios';
import { useAuthStore } from '@/store/modules/auth';
import { useRouterPush } from '@/hooks/common/router';
import { router as globalRouter } from '@/router';
import { localStg } from '@/utils/storage';
import { clearAuthStorage } from '@/store/modules/auth/shared';
import { fetchRefreshToken } from '../api';
import type { RequestInstanceState } from './type';

/**
 * refresh token
 *
 * @param axiosConfig - request config when the token is expired
 */
export async function handleRefreshToken(axiosConfig: AxiosRequestConfig) {
  const { resetStore } = useAuthStore();

  const refreshToken = localStg.get('refreshToken') || '';
  const { error, data } = await fetchRefreshToken(refreshToken);
  if (!error) {
    localStg.set('token', data.token);
    localStg.set('refreshToken', data.refreshToken);

    const config = { ...axiosConfig };
    if (config.headers) {
      config.headers.Authorization = data.token;
    }

    return config;
  }

  resetStore();

  return null;
}

/**
 * Handle HTTP-level errors (real status codes / network failure), which are NOT
 * covered by `onBackendFail` (that only fires when the request succeeds at the
 * HTTP layer but the business `code` indicates failure).
 *
 * - 401 not logged in -> reset auth and redirect to login page
 * - 403 permission denied -> stay on the current page (only show a message)
 * - server down / network error / 5xx -> redirect to the 500 error page
 *
 * @returns `true` if the error was handled by a redirect and the caller should
 *   stop (e.g. not show a duplicate error message)
 */
export function handleHttpError(error: AxiosError): boolean {
  // business-code failures (HTTP 200 + non-success code) are handled in `onBackendFail`
  if (error.code === BACKEND_ERROR_CODE) {
    return false;
  }

  // ignore canceled requests
  if (error.code === 'ERR_CANCELED') {
    return false;
  }

  const status = error.response?.status;

  // not logged in -> back to login（路由未挂载时仅清缓存，由后续路由守卫跳转登录）
  if (status === 401) {
    clearAuthStorage();
    if (globalRouter.currentRoute.value.name !== undefined) {
      useAuthStore().resetStore();
    }
    return true;
  }

  // permission insufficient -> do not redirect, let the caller show a message
  if (status === 403) {
    return false;
  }

  // no response (server down / network error) or server error -> error page
  if (!error.response || (status ?? 0) >= 500) {
    if (globalRouter.currentRoute.value.name !== undefined) {
      useRouterPush(false).routerPushByKey('500');
    }
    return true;
  }

  return false;
}

export function showErrorMsg(state: RequestInstanceState, message: string) {
  if (!state.errMsgStack?.length) {
    state.errMsgStack = [];
  }

  const isExist = state.errMsgStack.includes(message);

  if (!isExist) {
    state.errMsgStack.push(message);

    window.$message?.error(message, {
      onLeave: () => {
        state.errMsgStack = state.errMsgStack.filter(msg => msg !== message);

        setTimeout(() => {
          state.errMsgStack = [];
        }, 5000);
      }
    });
  }
}
